import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert, Box, Button, Card, CardContent, Chip, Divider, Grid2, IconButton,
  MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { Add, Delete, QrCodeScanner, Remove, Search } from '@mui/icons-material';
import { getItemsForPOS } from '../../../features/StockManagement/posSlice';
import { createBakeryDirectSale, createForwardInvoice, updateForwardInvoice } from '../../../services/bakeryPosApi';

const currency = (value) => `Rs. ${Number(value || 0).toLocaleString()}`;
const normalizeBarcode = (value) => String(value || '').trim().padStart(5, '0').toLowerCase();

export default function BakerySaleBuilder({ mode = 'forward', onCompleted, billingContext = null, editInvoice = null, onCancelEdit = null }) {
  const dispatch = useDispatch();
  const { MENU = [], isLoading } = useSelector((state) => state.pos);
  const { user } = useSelector((state) => state.auth);
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [scanValue, setScanValue] = useState('');
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const itemScanInputRef = useRef(null);
  const manualSearchInputRef = useRef(null);

  useEffect(() => {
    dispatch(getItemsForPOS({ procOrder: 'Get Items For POS', userID: user?.userID }));
  }, [dispatch, user?.userID]);

  useEffect(() => {
    if (!editInvoice) return;
    setCart((editInvoice.items || []).map((item) => ({
      id: Number(item.itemCode), name: item.itemName, price: Number(item.unitPrice), qty: Number(item.quantity),
    })));
    setCustomerName(editInvoice.customerName || '');
    setCustomerPhone(editInvoice.customerPhone || '');
    setDiscountAmount(Number(editInvoice.discountAmount || 0));
    setMessage(`Invoice ${editInvoice.tokenNo} editing ke liye load ho gayi.`);
  }, [editInvoice]);

  const categories = useMemo(() => [
    'All',
    ...Array.from(new Set(MENU.map((item) => String(item.category || 'Uncategorized').trim())))
      .filter(Boolean)
      .sort((left, right) => left.localeCompare(right)),
  ], [MENU]);

  useEffect(() => {
    if (!categories.includes(selectedCategory)) setSelectedCategory('All');
  }, [categories, selectedCategory]);

  const products = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return MENU.filter((item) => {
      const matchesCategory = selectedCategory === 'All'
        || String(item.category || 'Uncategorized').trim() === selectedCategory;
      const matchesSearch = !needle
        || String(item.name || '').toLowerCase().includes(needle)
        || String(item.id || '').includes(needle)
        || String(item.barCode || '').toLowerCase().includes(needle)
        || String(item.category || '').toLowerCase().includes(needle);
      return matchesCategory && matchesSearch;
    });
  }, [MENU, query, selectedCategory]);

  const subTotal = useMemo(
    () => cart.reduce((sum, item) => sum + Number(item.price) * Number(item.qty), 0),
    [cart]
  );
  const baseTotal = Math.max(0, subTotal - Number(discountAmount || 0));
  const checkoutTaxEnabled = mode === 'direct' && Boolean(billingContext?.taxModeEnabled);
  const taxAmount = checkoutTaxEnabled ? Number((baseTotal * Number(billingContext?.taxRate || 0) / 100).toFixed(2)) : 0;
  const grandTotal = Number((baseTotal + taxAmount).toFixed(2));

  const addItem = (product, quantity = 1) => {
    setCart((current) => {
      const found = current.find((item) => Number(item.id) === Number(product.id));
      return found
        ? current.map((item) => Number(item.id) === Number(product.id) ? { ...item, qty: item.qty + quantity } : item)
        : [...current, { ...product, qty: quantity }];
    });
  };

  const handleBarcodeScan = () => {
    const scanned = normalizeBarcode(scanValue);
    if (!scanned) return;
    const product = MENU.find((item) => [
      item.barCode,
      item.packBarCode,
      item.masterPackBarCode,
      item.id,
    ].some((value) => String(value || '').trim().toLowerCase() === scanned));
    if (!product) {
      setMessage(`Barcode "${scanValue}" kisi active item se match nahi hua. Manual search use kar sakte hain.`);
      setScanValue('');
      return;
    }
    const isMasterPack = String(product.masterPackBarCode || '').trim().toLowerCase() === scanned;
    const isPack = String(product.packBarCode || '').trim().toLowerCase() === scanned;
    const quantity = isMasterPack
      ? Math.max(1, Number(product.masterPackQuantity || 1))
      : isPack ? Math.max(1, Number(product.packQuantity || 1)) : 1;
    addItem(product, quantity);
    setMessage(`${product.name} cart mein add ho gaya${quantity > 1 ? ` (Qty ${quantity})` : ''}.`);
    setScanValue('');
  };
  const changeQty = (id, delta) => setCart((current) => current
    .map((item) => Number(item.id) === Number(id) ? { ...item, qty: Math.max(0, item.qty + delta) } : item)
    .filter((item) => item.qty > 0));

  const submit = async ({ printAfterSave = false } = {}) => {
    if (busy) return;
    if (!cart.length) return setMessage('Kam az kam ek item add karein.');
    if (!user?.userID) return setMessage('Logged-in user required hai.');
    setBusy(true);
    setMessage('');
    try {
      const wasEditing = Boolean(editInvoice);
      const payload = {
        userID: user.userID,
        salesmanUserID: user.userID,
        cashierUserID: user.userID,
        branchID: user.branchID || user.defaultBranchID || null,
        customerName,
        customerPhone,
        discountAmount: Number(discountAmount || 0),
        paymentMethod,
        loggedInUserID: user.userID,
        billingUserID: mode === 'direct' ? (billingContext?.activeBillingUserID || user.userID) : user.userID,
        taxModeEnabled: mode === 'direct' ? Boolean(billingContext?.taxModeEnabled) : false,
        taxAuthority: mode === 'direct' && billingContext?.taxModeEnabled ? billingContext?.billingLink?.taxAuthority : null,
        taxRate: mode === 'direct' ? Number(billingContext?.taxRate || 0) : 0,
        billingIdentityLinkID: mode === 'direct' ? (billingContext?.billingLink?.linkID || null) : null,
        taxProfileID: mode === 'direct' && billingContext?.taxModeEnabled ? (billingContext?.billingLink?.taxProfileID || null) : null,
        items: cart.map((item) => ({ itemCode: item.id, quantity: item.qty })),
      };
      const result = mode === 'direct'
        ? await createBakeryDirectSale(payload)
        : editInvoice
          ? await updateForwardInvoice(editInvoice.tokenNo, payload)
          : await createForwardInvoice(payload);
      setCart([]);
      setCustomerName('');
      setCustomerPhone('');
      setDiscountAmount(0);
      setMessage(mode === 'direct' ? `Direct sale completed. Order #${result.data.orderID}` : wasEditing ? 'Forward invoice updated.' : 'Forward invoice created.');
      if (onCompleted) await onCompleted(mode === 'direct' ? result.data : result, { printAfterSave, wasEditing });
    } catch (error) {
      setMessage(error?.response?.data?.message || error.message || 'Request complete nahi ho saki.');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (mode !== 'forward') return undefined;
    const handleShortcut = (event) => {
      if (event.repeat || !['F1', 'F3', 'F4'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'F1') {
        manualSearchInputRef.current?.focus();
        manualSearchInputRef.current?.select();
      } else if (event.key === 'F3') {
        itemScanInputRef.current?.focus();
        itemScanInputRef.current?.select();
      } else {
        submit({ printAfterSave: true });
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [mode, busy, cart, customerName, customerPhone, discountAmount, paymentMethod,
    editInvoice, user, billingContext, onCompleted]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Grid2 container spacing={2}>
      <Grid2 size={{ xs: 12, md: 7 }}>
        <Card variant="outlined">
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                autoFocus
                inputRef={itemScanInputRef}
                fullWidth
                size="small"
                label="Scan Item Barcode"
                placeholder="Scanner use karein ya barcode type karke Enter karein"
                value={scanValue}
                onChange={(event) => setScanValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleBarcodeScan();
                  }
                }}
                slotProps={{ input: { startAdornment: <QrCodeScanner sx={{ mr: 1, color: 'primary.main' }} /> } }}
              />
              <Button variant="contained" onClick={handleBarcodeScan} disabled={!scanValue.trim()}>
                Add
              </Button>
            </Stack>
            <TextField
              inputRef={manualSearchInputRef}
              fullWidth size="small" value={query} onChange={(event) => setQuery(event.target.value)}
              placeholder="Manual search: item name, category, code ya barcode"
              slotProps={{ input: { startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> } }}
              sx={{ mt: 1 }}
            />
            <Stack
              direction="row"
              spacing={0.75}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 1.25, maxHeight: 116, overflowY: 'auto', pb: 0.5 }}
            >
              {categories.map((category) => (
                <Chip
                  key={category}
                  label={category}
                  color={selectedCategory === category ? 'primary' : 'default'}
                  variant={selectedCategory === category ? 'filled' : 'outlined'}
                  onClick={() => setSelectedCategory(category)}
                  sx={{ fontWeight: selectedCategory === category ? 700 : 500 }}
                />
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              {products.length} item{products.length === 1 ? '' : 's'} - {selectedCategory}
            </Typography>
            <Grid2 container spacing={1.25} sx={{ mt: 1, maxHeight: 560, overflowY: 'auto' }}>
              {products.map((product) => (
                <Grid2 size={{ xs: 6, sm: 4 }} key={product.id}>
                  <Card
                    variant="outlined" onClick={() => addItem(product)}
                    sx={{ height: '100%', cursor: 'pointer', '&:hover': { borderColor: 'primary.main', boxShadow: 2 } }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography fontWeight={700} noWrap title={product.name}>{product.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{product.category}</Typography>
                      <Typography color="primary" fontWeight={700}>{currency(product.price)}</Typography>
                    </CardContent>
                  </Card>
                </Grid2>
              ))}
              {!isLoading && !products.length && <Typography sx={{ p: 2 }}>Koi item nahi mila.</Typography>}
            </Grid2>
          </CardContent>
        </Card>
      </Grid2>

      <Grid2 size={{ xs: 12, md: 5 }}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" fontWeight={700}>
              {mode === 'direct' ? 'New Direct Sale' : editInvoice ? `Edit Forward Invoice: ${editInvoice.tokenNo}` : 'Forward Invoice'}
            </Typography>
            <Stack spacing={1.25} sx={{ mt: 1.5 }}>
              <TextField size="small" label="Customer Name (Optional)" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
              <TextField size="small" label="Customer Phone (Optional)" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />
              <Divider />
              <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                {cart.map((item) => (
                  <Box key={item.id} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={700} noWrap>{item.name}</Typography>
                        <Typography variant="body2">{currency(item.price)} � {item.qty}</Typography>
                      </Box>
                      <Stack direction="row" alignItems="center">
                        <IconButton size="small" onClick={() => changeQty(item.id, -1)}><Remove /></IconButton>
                        <Chip size="small" label={item.qty} />
                        <IconButton size="small" onClick={() => changeQty(item.id, 1)}><Add /></IconButton>
                        <IconButton size="small" color="error" onClick={() => setCart((c) => c.filter((x) => x.id !== item.id))}><Delete /></IconButton>
                      </Stack>
                    </Stack>
                  </Box>
                ))}
                {!cart.length && <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Items select karein</Typography>}
              </Box>
              <TextField size="small" type="number" label="Overall Discount" value={discountAmount} onChange={(e) => setDiscountAmount(Math.max(0, Number(e.target.value || 0)))} />
              {mode === 'direct' && (
                <TextField select size="small" label="Payment Method" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                  <MenuItem value="wallet">Wallet</MenuItem>
                  <MenuItem value="bank">Bank</MenuItem>
                  <MenuItem value="voucher">Voucher</MenuItem>
                </TextField>
              )}
              <Stack direction="row" justifyContent="space-between"><Typography>Subtotal</Typography><Typography>{currency(subTotal)}</Typography></Stack>
              {checkoutTaxEnabled && <Stack direction="row" justifyContent="space-between"><Typography>{billingContext?.billingLink?.taxAuthority} Tax ({billingContext?.taxRate}%)</Typography><Typography>{currency(taxAmount)}</Typography></Stack>}
              <Stack direction="row" justifyContent="space-between"><Typography fontWeight={800}>Grand Total</Typography><Typography fontWeight={800}>{currency(grandTotal)}</Typography></Stack>
              {message && <Alert severity={message.includes('completed') || message.includes('created') ? 'success' : 'info'}>{message}</Alert>}
              <Button variant="contained" size="large" disabled={busy || !cart.length} onClick={() => submit({ printAfterSave: mode === 'forward' })}>
                {busy ? 'Please wait...' : mode === 'direct' ? 'Receive Payment & Complete' : editInvoice ? 'Update & Print Invoice' : 'Save & Print Forward Invoice'}
              </Button>
              {editInvoice && <Button variant={'outlined'} disabled={busy} onClick={onCancelEdit}>Cancel Edit</Button>}
            </Stack>
          </CardContent>
        </Card>
      </Grid2>
    </Grid2>
  );
}

